import styled, { css } from 'styled-components';
import { complementaryColor, fontSize, tertiaryColor, warningColor } from '@styles/utils';
import { forwardRef, useState } from 'react';
import showPasswordIcon from '@assets/icons/auth/show-password.svg';
import hidePasswordIcon from '@assets/icons/auth/hide-password.svg';

interface InputProps {
  error?: boolean;
}

export const Input = styled.input.attrs(props => ({
  type: props.type ?? 'text',
}))<InputProps>`
  display: block;
  height: 50px;
  width: 100%;
  padding: 0 20px;
  margin: 0;
  border: 1px solid ${tertiaryColor(300)};
  border-radius: 2px;
  box-sizing: border-box;
  font-family: inherit;
  ${fontSize.label};
  color: ${tertiaryColor(700)};
  line-height: 1;
  outline: none;
  background-color: ${tertiaryColor(100)};

  &::placeholder {
    font-family: inherit;
    ${fontSize.label};
    opacity: 0.4;
    color: ${tertiaryColor(700)};
  }

  &:disabled {
    color: ${tertiaryColor(700)};
    background-color: ${complementaryColor('white')};
    cursor: not-allowed;
    border: none;
  }

  ${props =>
    props.error &&
    css`
      border-color: ${warningColor()};
    `}
`;

const PasswordInputContainer = styled.div`
  position: relative;

  > button {
    position: absolute;
    right: 20px;
    top: 50%;
    transform: translateY(-50%);
    width: 22px;
    height: 14px;
    background: url(${showPasswordIcon.src}) no-repeat center center;
    border: none;
    outline: none;
    cursor: pointer;

    &.show {
      background: url(${hidePasswordIcon.src}) no-repeat center center;

      &:after {
        display: none;
      }
    }
  }
`;

export const PasswordInput = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement> & InputProps>(
  ({ className, ...props }, ref) => {
    const [showPassword, setShowPassword] = useState<boolean>(false);

    const toggleShowPassword = () => setShowPassword(old => !old);

    return (
      <PasswordInputContainer className={className}>
        <Input type={showPassword ? 'text' : 'password'} ref={ref} {...props} />
        <button type="button" className={showPassword ? 'show' : ''} tabIndex={-1} onClick={toggleShowPassword} />
      </PasswordInputContainer>
    );
  },
);
